import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Select, Table, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { orgService, type MemberInfo, type RoleInfo } from "@/services/organization";
import { UserAvatar } from "@/components/UserAvatar";
import { useCardExtra } from "@/layouts/ManagementLayout";
import { formatDateTime } from "@/utils/date";

const P = { INVITE: "OrgMemberInvite", REMOVE: "OrgMemberRemove", ROLE: "OrgRoleManage" };

export function MemberManagement() {
  const { t } = useTranslation("management");
  const { context, hasPermission, permissions } = useManagementStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [m, r] = await Promise.all([
        orgService.listMembers(orgId),
        orgService.listRoles(orgId),
      ]);
      setMembers(m);
      setRoles(r);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  const handleInvite = async () => {
    const values = await form.validateFields();
    try {
      await orgService.inviteMember(orgId, values.account, values.role_id);
      message.success(t("common:success"));
      setInviteOpen(false);
      form.resetFields();
      load();
    } catch (e) { message.error(e instanceof Error ? e.message : t("common:error")); }
  };

  const handleChangeRole = async (userId: string, roleId: string) => {
    try {
      await orgService.updateMember(orgId, userId, roleId);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
  };

  const handleRemove = async (userId: string) => {
    try {
      await orgService.removeMember(orgId, userId);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
  };

  const roleMap = Object.fromEntries(roles.map((r) => [r.id, r.name]));

  const columns = [
    {
      title: t("members.table.name"), key: "name",
      render: (_: unknown, record: MemberInfo) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UserAvatar name={record.user_name || "?"} size={24} />
          {record.user_name || "—"}
        </span>
      ),
    },
    { title: t("members.table.account"), dataIndex: "user_account", key: "account" },
    {
      title: t("members.table.role"), key: "role",
      render: (_: unknown, record: MemberInfo) =>
        hasPermission(P.ROLE) ? (
          <Select
            value={record.role_id}
            onChange={(v) => handleChangeRole(record.user_id, v)}
            size="small"
            style={{ width: 140 }}
            options={roles.map((r) => ({ label: r.name, value: r.id }))}
          />
        ) : (
          <span>{roleMap[record.role_id] || record.role_id}</span>
        ),
    },
    {
      title: t("members.table.joined_at"), dataIndex: "joined_at", key: "joined_at",
      render: (v: string) => formatDateTime(v),
    },
    ...(hasPermission(P.REMOVE) ? [{
      title: t("common:action"), key: "action", width: 60,
      render: (_: unknown, record: MemberInfo) => (
        <Popconfirm title={t("members.remove.confirm")} onConfirm={() => handleRemove(record.user_id)}>
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    }] : []),
  ];

  useCardExtra(
    hasPermission(P.INVITE) ? (
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setInviteOpen(true)}>
        {t("members.invite")}
      </Button>
    ) : null,
    [t, permissions],
  );

  return (
    <>
      <Table dataSource={members} columns={columns} rowKey="id" loading={loading} size="middle" />
      <Modal title={t("members.invite")} open={inviteOpen} onOk={handleInvite} onCancel={() => setInviteOpen(false)}>
        <Form form={form} layout="vertical">
          <Form.Item label={t("members.invite.account")} name="account" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("members.invite.role")} name="role_id">
            <Select
              placeholder={t("members.invite.role")}
              options={roles.map((r) => ({ label: `${r.name}${r.is_default ? " (default)" : ""}`, value: r.id }))}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
